import React, { useRef, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { COLORS } from '../settings/variables/Colors';
import { SPACERS } from '../settings/variables/Spacers';
import { textSelectionNone } from '../base/TrueImpactText';

interface TrueImpactFoldButtonProps<T> {
  selectedValue: T;
  allowTextSelection?: boolean;
  buttons: {
    value: T;
    text: string;
    color?: keyof typeof COLORS;
    backgroundColor: keyof typeof COLORS;
    disabled?: boolean;
  }[];
  onClick: (value: T) => unknown;
  backgroundColor?: keyof typeof COLORS;
}

const borderRadius = SPACERS.tiny;
const StyledItemDiv = styled.div<{
  enabled: boolean;
  isLastIndex: boolean;
  isFirstIndex: boolean;
  allowTextSelection: boolean;
  isDisabled: boolean;
  color: keyof typeof COLORS;
  backgroundColor: keyof typeof COLORS;
}>`
  z-index: 1;
  font-size: 14px;
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  ${({ isDisabled }) => (isDisabled ? 'opacity: 0.45;' : '')}
  position: relative;
  transition: all 0.3s ease;
  border-radius: ${({ isFirstIndex, isLastIndex }) =>
    isFirstIndex
      ? `${borderRadius} 0 0 ${borderRadius}`
      : isLastIndex
      ? `0 ${borderRadius} ${borderRadius} 0`
      : '0'};
  padding: ${SPACERS.tiny} ${SPACERS.small};
  ${({ allowTextSelection }) => (allowTextSelection ? '' : textSelectionNone)}
  ${({ enabled, color, backgroundColor }) =>
    enabled
      ? css`
          color: ${COLORS[color]};
          background-color: ${COLORS[backgroundColor]};
        `
      : css`
          color: inherit;
          background-color: inherit;
        `};
`;

const StyledDiv = styled.div<{ backgroundColor: keyof typeof COLORS }>`
  display: flex;
  position: relative;
  border-radius: ${borderRadius};
  background-color: ${({ backgroundColor }) => COLORS[backgroundColor]};
`;

const Slider = styled.div<{
  width: number;
  left: number;
  color: string;
  backgroundColor: string;
}>`
  top: 4px;
  z-index: 0;
  position: absolute;
  border-radius: 4px;
  height: calc(100% - 8px);
  left: ${({ left }) => left}px;
  width: ${({ width }) => width}px;
  background-color: ${({ color }) => color};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const TrueImpactFoldButton = <T,>({
  buttons,
  selectedValue,
  onClick,
  allowTextSelection = false,
  backgroundColor = 'grayLight',
}: TrueImpactFoldButtonProps<T>) => {
  const buttonRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [slider, setSlider] = useState({
    left: 0,
    width: 0,
    color: COLORS.ctaPrimary,
    backgroundColor: 'inherit',
  });

  useEffect(() => {
    const selectedIndex = buttons.findIndex(b => b.value === selectedValue);
    if (selectedIndex === -1 || !buttonRefs.current[selectedIndex]) return;

    const selectedButton = buttonRefs.current[selectedIndex];
    if (!selectedButton) return;

    const selectedColor = buttons[selectedIndex].color;
    setSlider({
      width: selectedButton.offsetWidth,
      left: selectedButton.offsetLeft,
      color: selectedColor ? COLORS[selectedColor] : 'inherit',
      backgroundColor: buttons[selectedIndex].backgroundColor
        ? COLORS[buttons[selectedIndex].backgroundColor]
        : 'inherit',
    });
  }, [selectedValue, buttons]);

  return (
    <StyledDiv backgroundColor={backgroundColor}>
      <Slider
        width={slider.width}
        left={slider.left}
        color={slider.color}
        backgroundColor={slider.backgroundColor}
      />
      {buttons.map((b, index) => {
        const isFirstIndex = index === 0;
        const isLastIndex = index === buttons.length - 1;
        return (
          <StyledItemDiv
            key={b.text}
            color={b.color || 'blackBase'}
            isDisabled={Boolean(b.disabled)}
            aria-disabled={Boolean(b.disabled)}
            onClick={() => {
              if (!b.disabled) onClick(b.value);
            }}
            allowTextSelection={allowTextSelection}
            isFirstIndex={isFirstIndex}
            isLastIndex={isLastIndex}
            backgroundColor={b.backgroundColor}
            enabled={b.value === selectedValue}
            ref={el => {
              buttonRefs.current[index] = el;
            }}
          >
            {b.text}
          </StyledItemDiv>
        );
      })}
    </StyledDiv>
  );
};

export default TrueImpactFoldButton;
